import { useState, useEffect, useCallback } from 'react';
import { preferencesAPI } from '@/lib/supabase/preferences';
import { campaignAPI } from '@/lib/supabase/campaigns';

const SKILL_LEVELS = {
  1: { label: '1-High', color: 'error' },
  2: { label: '2-Medium', color: 'warning' },
  3: { label: '3-Low', color: 'success' }
};

export const useCampaignPriorities = (userId?: string) => {
  const [priorities, setPriorities] = useState<Record<string, number | null>>({});
  const [mobileEligibility, setMobileEligibility] = useState<Record<string, boolean>>({});
  const [campaignStatus, setCampaignStatus] = useState<
    Record<string, 'Available' | 'Advance Notice' | 'Paused' | null>
  >({});
  const [campaignStatusReasons, setCampaignStatusReasons] = useState<Record<string, string | null>>(
    {}
  );
  // Track which fields have been manually set by the user (vs auto-detected)
  const [userSetFields, setUserSetFields] = useState<{
    status: Set<string>;
    skillLevel: Set<string>;
    mobileEligibility: Set<string>;
  }>({
    status: new Set(),
    skillLevel: new Set(),
    mobileEligibility: new Set()
  });
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOnline, setIsOnline] = useState(true);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Load preferences from localStorage as fallback
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedPriorities = localStorage.getItem('campaignPriorities');
      const savedMobileEligibility = localStorage.getItem('campaignMobileEligibility');
      const savedCampaignStatus = localStorage.getItem('campaignStatus');
      const savedCampaignStatusReasons = localStorage.getItem('campaignStatusReasons');

      return {
        priorities: savedPriorities ? JSON.parse(savedPriorities) : {},
        mobileEligibility: savedMobileEligibility ? JSON.parse(savedMobileEligibility) : {},
        campaignStatus: savedCampaignStatus ? JSON.parse(savedCampaignStatus) : {},
        campaignStatusReasons: savedCampaignStatusReasons
          ? JSON.parse(savedCampaignStatusReasons)
          : {}
      };
    } catch (error) {}
    return { priorities: {}, mobileEligibility: {}, campaignStatus: {}, campaignStatusReasons: {} };
  }, []);

  // Save to localStorage as backup
  const saveToLocalStorage = useCallback((preferences: any) => {
    try {
      const prioritiesData = preferences.priorities || preferences.skillLevels || {};
      localStorage.setItem('campaignPriorities', JSON.stringify(prioritiesData));
      localStorage.setItem(
        'campaignMobileEligibility',
        JSON.stringify(preferences.mobileEligibility)
      );
      if (preferences.campaignStatus !== undefined) {
        localStorage.setItem('campaignStatus', JSON.stringify(preferences.campaignStatus));
      }
      if (preferences.campaignStatusReasons !== undefined) {
        localStorage.setItem(
          'campaignStatusReasons',
          JSON.stringify(preferences.campaignStatusReasons)
        );
      }
    } catch (error) {}
  }, []);

  // Load campaigns and preferences
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load campaigns (with dealer preferences if user is authenticated)
        const campaignsData = userId
          ? await campaignAPI.getCampaignsWithPreferences(userId)
          : await campaignAPI.getCampaigns();
        setCampaigns(campaignsData);

        if (userId && isOnline) {
          // Try to load from Supabase
          const preferences = await preferencesAPI.getCampaignPreferences(userId);
          setPriorities(preferences.skillLevels);
          setMobileEligibility(preferences.mobileEligibility);

          // Auto-detect campaigns with advance notice and set status if not manually set
          const autoDetectedStatus: Record<
            string,
            'Available' | 'Advance Notice' | 'Paused' | null
          > = {};
          campaignsData.forEach(campaign => {
            const campaignNumber = campaign.campaign_number;
            const userSetStatus = preferences.campaignStatus?.[campaignNumber];

            // Only auto-set if user hasn't manually set a status
            if (!userSetStatus && campaign.has_advance_notice_recalls) {
              autoDetectedStatus[campaignNumber] = 'Advance Notice';
            } else if (!userSetStatus) {
              autoDetectedStatus[campaignNumber] = 'Available';
            }
          });

          // Merge user-set statuses with auto-detected ones (user-set takes priority)
          const mergedStatus = {
            ...autoDetectedStatus,
            ...(preferences.campaignStatus || {})
          };

          setCampaignStatus(mergedStatus);
          setCampaignStatusReasons(preferences.campaignStatusReasons || {});

          // Track which fields are user-set
          setUserSetFields({
            status: new Set(Object.keys(preferences.campaignStatus || {})),
            skillLevel: new Set(Object.keys(preferences.skillLevels || {})),
            mobileEligibility: new Set(Object.keys(preferences.mobileEligibility || {}))
          });
          // Also save to localStorage as backup
          saveToLocalStorage({
            ...preferences,
            campaignStatus: mergedStatus
          });
        } else {
          // Fallback to localStorage
          const preferences = loadFromLocalStorage();
          setPriorities(preferences.priorities);
          setMobileEligibility(preferences.mobileEligibility);

          // Auto-detect campaigns with advance notice
          const autoDetectedStatus: Record<
            string,
            'Available' | 'Advance Notice' | 'Paused' | null
          > = {};
          campaignsData.forEach(campaign => {
            const campaignNumber = campaign.campaign_number;
            const userSetStatus = preferences.campaignStatus?.[campaignNumber];

            if (!userSetStatus && campaign.has_advance_notice_recalls) {
              autoDetectedStatus[campaignNumber] = 'Advance Notice';
            } else if (!userSetStatus) {
              autoDetectedStatus[campaignNumber] = 'Available';
            }
          });

          const mergedStatus = {
            ...autoDetectedStatus,
            ...(preferences.campaignStatus || {})
          };

          setCampaignStatus(mergedStatus);
          setCampaignStatusReasons(preferences.campaignStatusReasons || {});
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load campaign data');

        // Fallback to localStorage on error
        const preferences = loadFromLocalStorage();
        setPriorities(preferences.priorities);
        setMobileEligibility(preferences.mobileEligibility);
        setCampaignStatus(preferences.campaignStatus || {});
        setCampaignStatusReasons(preferences.campaignStatusReasons || {});

        // Track which fields are user-set (from localStorage, assume all are user-set if in localStorage)
        setUserSetFields({
          status: new Set(Object.keys(preferences.campaignStatus || {})),
          skillLevel: new Set(Object.keys(preferences.priorities || {})),
          mobileEligibility: new Set(Object.keys(preferences.mobileEligibility || {}))
        });
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [userId, isOnline, loadFromLocalStorage, saveToLocalStorage]);

  const updatePriority = useCallback(
    async (campaignNumber: string, priority: number | null) => {
      if (!campaignNumber) return;

      const priorityValue = priority === null ? null : parseInt(priority.toString());

      // Optimistic update
      const newPriorities = {
        ...priorities,
        [campaignNumber]: priorityValue
      };
      setPriorities(newPriorities);

      // Save to localStorage immediately
      saveToLocalStorage({
        priorities: newPriorities,
        mobileEligibility,
        campaignStatus,
        campaignStatusReasons
      });

      // Track as user-set field
      setUserSetFields(prev => {
        const newSet = new Set(prev.skillLevel);
        if (priorityValue !== null) {
          newSet.add(campaignNumber);
        } else {
          newSet.delete(campaignNumber);
        }
        return { ...prev, skillLevel: newSet };
      });

      // Try to save to Supabase if online and user is authenticated
      if (userId && isOnline) {
        try {
          await preferencesAPI.updateCampaignSkillLevel(userId, campaignNumber, priorityValue);
        } catch (err) {
          // Don't revert the optimistic update - localStorage has the data
          setError(err instanceof Error ? err.message : 'Failed to save to Supabase');
        }
      }
    },
    [priorities, mobileEligibility, userId, isOnline, saveToLocalStorage]
  );

  const updateMobileServicingEligibility = useCallback(
    async (campaignNumber: string, mobileServicingEligible: boolean) => {
      if (!campaignNumber) return;

      // Optimistic update
      const newMobileEligibility = {
        ...mobileEligibility,
        [campaignNumber]: mobileServicingEligible
      };
      setMobileEligibility(newMobileEligibility);

      // Save to localStorage immediately
      saveToLocalStorage({
        priorities,
        mobileEligibility: newMobileEligibility,
        campaignStatus,
        campaignStatusReasons
      });

      // Track as user-set field
      setUserSetFields(prev => {
        const newSet = new Set(prev.mobileEligibility);
        newSet.add(campaignNumber);
        return { ...prev, mobileEligibility: newSet };
      });

      // Try to save to Supabase if online and user is authenticated
      if (userId && isOnline) {
        try {
          await preferencesAPI.updateCampaignMobileServicingEligibility(
            userId,
            campaignNumber,
            mobileServicingEligible
          );
        } catch (err) {
          // Don't revert the optimistic update - localStorage has the data
          setError(err instanceof Error ? err.message : 'Failed to save to Supabase');
        }
      }
    },
    [
      mobileEligibility,
      priorities,
      campaignStatus,
      campaignStatusReasons,
      userId,
      isOnline,
      saveToLocalStorage
    ]
  );

  const updateCampaignStatus = useCallback(
    async (
      campaignNumber: string,
      status: 'Available' | 'Advance Notice' | 'Paused',
      statusReason?: string | null
    ) => {
      if (!campaignNumber) return;

      // Optimistic update
      const newCampaignStatus = {
        ...campaignStatus,
        [campaignNumber]: status
      };
      setCampaignStatus(newCampaignStatus);

      const newCampaignStatusReasons = {
        ...campaignStatusReasons,
        [campaignNumber]: statusReason || null
      };
      setCampaignStatusReasons(newCampaignStatusReasons);

      // Save to localStorage immediately
      saveToLocalStorage({
        priorities,
        mobileEligibility,
        campaignStatus: newCampaignStatus,
        campaignStatusReasons: newCampaignStatusReasons
      });

      // Track as user-set field
      setUserSetFields(prev => {
        const newSet = new Set(prev.status);
        newSet.add(campaignNumber);
        return { ...prev, status: newSet };
      });

      // Try to save to Supabase if online and user is authenticated
      if (userId && isOnline) {
        try {
          await preferencesAPI.updateCampaignStatus(userId, campaignNumber, status, statusReason);
        } catch (err) {
          // Don't revert the optimistic update - localStorage has the data
          setError(err instanceof Error ? err.message : 'Failed to save to Supabase');
        }
      }
    },
    [
      campaignStatus,
      campaignStatusReasons,
      priorities,
      mobileEligibility,
      userId,
      isOnline,
      saveToLocalStorage
    ]
  );

  const resetAllMobileServicingEligibility = useCallback(async () => {
    // Reset all mobile servicing eligibility to false
    const resetMobileEligibility: Record<string, boolean> = {};

    // Get all campaign numbers from priorities
    Object.keys(priorities).forEach(campaignNumber => {
      resetMobileEligibility[campaignNumber] = false;
    });

    // Optimistic update
    setMobileEligibility(resetMobileEligibility);

    // Save to localStorage immediately
    saveToLocalStorage({
      priorities,
      mobileEligibility: resetMobileEligibility,
      campaignStatus,
      campaignStatusReasons
    });

    // Try to save to Supabase if online and user is authenticated
    if (userId && isOnline) {
      try {
        // Batch update all campaigns to false
        const resetPromises = Object.keys(priorities).map(campaignNumber =>
          preferencesAPI.updateCampaignMobileServicingEligibility(userId, campaignNumber, false)
        );
        await Promise.all(resetPromises);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save to Supabase');
      }
    }
  }, [priorities, userId, isOnline, saveToLocalStorage]);

  const getPriority = useCallback(
    (campaignNumber: string) => {
      return priorities[campaignNumber] || null;
    },
    [priorities]
  );

  const getPriorityConfig = useCallback((priority: number | null) => {
    return (
      SKILL_LEVELS[priority as keyof typeof SKILL_LEVELS] || { label: 'Not Set', color: 'default' }
    );
  }, []);

  return {
    priorities,
    mobileEligibility,
    campaignStatus,
    campaignStatusReasons,
    campaigns,
    updatePriority,
    updateMobileServicingEligibility,
    updateCampaignStatus,
    resetAllMobileServicingEligibility,
    getPriority,
    getPriorityConfig,
    loading,
    error,
    isOnline,
    isAuthenticated: !!userId,
    SKILL_LEVELS,
    userSetFields
  };
};
